import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isMumbaiCorridorFeed } from "../../lib/tokens";
const NAV = [
    { key: "overview", label: "Overview" },
    { key: "demand", label: "Demand" },
    { key: "sim", label: "Sim" },
    { key: "docs", label: "Docs" },
];
function routeMetaLabel(envelope) {
    if (!envelope)
        return "Route AS-4 · Mumbai";
    if (!envelope.simulated)
        return "Delhi · DTC/DIMTS · Live";
    if (isMumbaiCorridorFeed(envelope.entities))
        return "Mumbai · MMR Corridor";
    return "Route AS-4 · Mumbai";
}
const rootStyle = {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "14px 20px",
    backgroundColor: "var(--color-pure-white)",
    borderBottom: "1px solid var(--color-pebble)",
    zIndex: 10,
    minWidth: 0,
    flexWrap: "wrap",
};
const wordmarkStyle = {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 6,
    fontFamily: "var(--font-display)",
    fontWeight: 700,
    fontSize: 20,
    letterSpacing: "-0.02em",
    lineHeight: 1,
    color: "var(--color-deep-ink)",
    whiteSpace: "nowrap",
};
const wordmarkDotStyle = {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "var(--color-clay-ember)",
    display: "inline-block",
    marginRight: 4,
};
const navStyle = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: 4,
    borderRadius: 1000,
    border: "1px solid var(--color-pebble)",
    backgroundColor: "var(--color-pure-white)",
    overflowX: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
};
const pillStyle = (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 16px",
    borderRadius: 1000,
    fontFamily: "var(--font-display)",
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1,
    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
    backgroundColor: active ? "var(--color-deep-ink)" : "transparent",
    color: active ? "var(--color-pure-white)" : "var(--color-deep-ink)",
    transition: "background-color 120ms ease-out, color 120ms ease-out, opacity 120ms ease-out",
    cursor: "pointer",
    border: 0,
});
const routeMetaStyle = {
    display: "none",
    fontFamily: "var(--font-display)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "var(--color-cool-ash)",
    whiteSpace: "nowrap",
};
function specFor(state, err, attempts) {
    switch (state) {
        case "connected":
            return {
                color: "#3fa66a",
                label: "Live",
                pulsing: true,
                title: "WebSocket connected · streaming live envelope every 3s",
            };
        case "polling":
            return {
                color: "#8e8e95",
                label: "Polling",
                pulsing: false,
                title: `WebSocket offline · falling back to REST poll (attempt ${attempts})`,
            };
        case "connecting":
            return {
                color: "#8e8e95",
                label: "Connecting",
                pulsing: true,
                title: "Opening WebSocket to /ws/vehicles…",
            };
        case "disconnected":
        default:
            return {
                color: "#bc7155",
                label: "Offline",
                pulsing: false,
                title: err ? `Backend unreachable · ${err}` : "Backend unreachable",
            };
    }
}
export function Header({ activeKey = "overview", onNavChange, connectionState = "connecting", connectionError = null, reconnectAttempts = 0, envelope, carpoolImpact, }) {
    const spec = specFor(connectionState, connectionError, reconnectAttempts);
    const routeMeta = routeMetaLabel(envelope);
    const showImpact = (carpoolImpact?.pools_today ?? 0) > 0 &&
        (carpoolImpact?.co2_kg ?? 0) > 0;
    return (_jsxs("header", { style: rootStyle, "data-testid": "app-header", children: [_jsxs("div", { className: "bb-header-brand", style: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 }, children: [_jsxs("span", { style: wordmarkStyle, "data-testid": "wordmark", children: [_jsx("span", { style: wordmarkDotStyle, "aria-hidden": "true" }), "Bunching Brain", _jsx("sup", { style: {
                                    fontSize: 9,
                                    marginLeft: 2,
                                    color: "var(--color-cool-ash)",
                                    fontWeight: 700,
                                    verticalAlign: "super",
                                }, children: "(R)" }), "."] }), _jsx("span", { className: "bb-header-divider", style: {
                            display: "inline-block",
                            width: 1,
                            height: 20,
                            backgroundColor: "var(--color-pebble)",
                        }, "aria-hidden": "true" }), _jsx("span", { className: "bb-header-microlabel", style: {
                            fontFamily: "var(--font-display)",
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: "var(--color-cool-ash)",
                            whiteSpace: "nowrap",
                        }, children: "Operator Control Tower" })] }), _jsx("nav", { style: navStyle, "aria-label": "Primary", "data-testid": "primary-nav", className: "bb-header-nav", children: NAV.map((item) => {
                    const active = item.key === activeKey;
                    return (_jsx("button", { type: "button", onClick: () => onNavChange?.(item.key), "aria-current": active ? "page" : undefined, style: pillStyle(active), "data-testid": `nav-${item.key}`, "data-active": active ? "true" : "false", children: item.label }, item.key));
                }) }), _jsxs("div", { style: {
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    minWidth: 0,
                }, children: [showImpact && carpoolImpact ? (_jsxs("div", { "data-testid": "carpool-impact-ticker", title: "Cumulative pool merges today \u00B7 CO\u2082 + INR saved \u00B7 Phase E \u00B7 Convergence", style: {
                            display: "inline-flex",
                            alignItems: "baseline",
                            gap: 10,
                            padding: "6px 14px",
                            border: "1.5px solid var(--color-clay-ember)",
                            borderRadius: 1000,
                            background: "var(--color-pure-white)",
                            color: "var(--color-clay-ember)",
                            fontFamily: "var(--font-mono, monospace)",
                            fontSize: 12,
                            fontWeight: 800,
                            letterSpacing: "0.06em",
                            whiteSpace: "nowrap",
                            fontVariantNumeric: "tabular-nums",
                        }, children: [_jsxs("span", { children: [carpoolImpact.pools_today, " POOLS"] }), _jsx("span", { style: { opacity: 0.4 }, children: "\u00B7" }), _jsxs("span", { children: [carpoolImpact.co2_kg.toFixed(1), " kg CO\u2082"] }), _jsx("span", { style: { opacity: 0.4 }, children: "\u00B7" }), _jsxs("span", { children: ["\u20B9", Math.round(carpoolImpact.inr_saved).toLocaleString("en-IN")] })] })) : null, _jsx("div", { style: routeMetaStyle, className: "header-route-meta", children: routeMeta }), _jsxs("div", { "data-testid": "live-feed-indicator", "data-connection-state": connectionState, title: spec.title, style: {
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 12px",
                            border: "1px solid var(--color-pebble)",
                            borderRadius: 1000,
                            fontFamily: "var(--font-display)",
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: "var(--color-cool-ash)",
                            backgroundColor: "var(--color-pure-white)",
                            whiteSpace: "nowrap",
                        }, children: [_jsx("span", { "aria-hidden": "true", "data-testid": "live-feed-dot", style: {
                                    display: "inline-block",
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: spec.color,
                                    boxShadow: "none",
                                    animation: spec.pulsing ? "bb-live-pulse 1.4s ease-in-out infinite" : "none",
                                } }), _jsx("span", { children: spec.label })] })] }), _jsx("style", { children: `
        @media (max-width: 620px) {
          .bb-header-divider,
          .bb-header-microlabel { display: none !important; }
          .bb-header-nav {
            flex-basis: 100%;
            order: 3;
            justify-content: flex-start;
          }
        }
        @media (max-width: 380px) {
          [data-testid="wordmark"] { font-size: 18px !important; }
        }
        @keyframes bb-live-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(0.75); }
        }
      ` })] }));
}
